/**
 * Subject + body of one InMail, with "✨ rewrite" and one level of undo.
 *
 * A `MessageCard`: plain text plus links shown as links (Oct 22 2026), which the extension types
 * into LinkedIn's composer as real links. The ✨ field is a plain `<input>`
 * inside a `<label>`, the same documented deviation as EmailBodyEditor's prompt (the DS Input hides
 * its placeholder until focus, which made that field look unusable).
 */
import { useState } from 'react';
import { MessageCard } from './MessageCard';
import { RewriteBar } from './RewriteBar';

const visibleLength = (text: string) => `${text || ''}`.replace(/\[([^\]\n]+)\]\(([^)\s]+)\)/g, '$1').length;

export function OutreachDraftEditor({
  candidateId,
  subject,
  body,
  warnings,
  disabled,
  onChange,
  maxBody,
  maxSubject,
}: {
  candidateId: string;
  subject: string;
  body: string;
  warnings: string[];
  disabled: boolean;
  onChange: (next: { subject: string; body: string }) => void;
  maxBody: number;
  maxSubject: number;
}) {
  const [rewriteWarnings, setRewriteWarnings] = useState<string[]>([]);

  const allWarnings = [...warnings, ...rewriteWarnings];
  return (
    <div className="space-y-2">
      <MessageCard
        title="InMail"
        subject={subject}
        onSubject={(v) => onChange({ subject: v, body })}
        body={body}
        onBody={(v) => onChange({ subject, body: v })}
        disabled={disabled}
        minRows={8}
        assist={
          <RewriteBar
            kind="inmail"
            candidateId={candidateId}
            subject={subject}
            body={body}
            disabled={disabled}
            placeholder="Help me write: shorter, warmer, in German, mention the team size…"
            onApply={(r) => {
              onChange({ subject: r.subject, body: r.body });
              setRewriteWarnings(r.warnings);
            }}
          />
        }
        footer={
          !disabled && (
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-[#707070]">
              <span>
                {/* What the reader sees: a link counts as its words (Oct 22 2026). */}
                <span className={visibleLength(body) > maxBody ? 'font-semibold text-[#B3261E]' : ''}>
                  {visibleLength(body)}/{maxBody} characters
                </span>
                {' · '}
                <span className={subject.length > maxSubject ? 'font-semibold text-[#B3261E]' : ''}>
                  subject {subject.length}/{maxSubject}
                </span>
              </span>
            </div>
          )
        }
      />
      {allWarnings.length > 0 && (
        <ul className="rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-3 py-2 text-[13px] text-[#7A4B00]">
          {allWarnings.map((w) => (
            <li key={w}>⚠ {w}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
